import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Icons,
  Modal,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { EDUCATOR_ROLE, STUDENT_ROLE } from '@core/constants/space';
import { HomeViewRole } from '@app/constants/home';

const MODAL_TITLE_ID = 'upcoming-assignments-info-modal-title';

const STRINGS = {
  title: 'Assignments',
  close: 'Close',
  items: [
    {
      id: 'upcoming',
      title: 'Due soon assignment',
      description:
        'Assignments due within the next 7 days are highlighted so you can quickly identify work that needs your attention.',
    },
    {
      id: 'open',
      title: 'Open assignment',
      description:
        'Assignments that are open and have more than 7 days remaining until the due date are shown as Open.',
    },
    {
      id: 'hidden',
      title: 'Hidden assignment',
      description:
        'These assignments have a due date but are not yet open to students. Review them and publish them before the due date, if required.',
    },
    {
      id: 'submitted',
      title: 'Submitted assignment',
      description:
        'The icon shows assignments you have already submitted. Submitted assignments remain visible until the due date, so you can review your submission or check the assignment details.',
    },
  ],
};

type UpcomingAssignmentsInfoModalProps = {
  open: boolean;
  onClose: () => void;
  viewRole: HomeViewRole;
};

type LegendRowProps = {
  children: React.ReactNode;
  title: string;
  description: string;
};

const LegendRow = ({ children, title, description }: LegendRowProps) => (
  <Box display="flex" gap="5px" alignItems="flex-start">
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width={24}
      minWidth={24}
      pt={0.25}
      tabIndex={0}
      aria-label={title}
    >
      {children}
    </Box>
    <Box flex={1}>
      <Typography
        variant="subtitle1"
        fontWeight={500}
        fontSize="16px"
        color="text.primary"
        tabIndex={0}
        sx={{ lineHeight: '150%', letterSpacing: '0.15px' }}
        aria-label={title}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        fontWeight={400}
        sx={{ lineHeight: '180%', letterSpacing: '0.17px' }}
        mt={0.5}
        tabIndex={0}
        aria-label={description}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

const UpcomingAssignmentsInfoModal = ({
  open,
  onClose,
  viewRole,
}: UpcomingAssignmentsInfoModalProps) => {
  const { palette } = useTheme();
  const showHiddenLegend = viewRole === EDUCATOR_ROLE;
  const showSubmittedLegend = viewRole === STUDENT_ROLE;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby={MODAL_TITLE_ID}>
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby={MODAL_TITLE_ID}
        sx={{
          width: { xs: 'calc(100vw - 32px)', sm: 560 },
          bgcolor: palette.background.paper,
          borderRadius: '8px',
          boxShadow: 24,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          px={{ xs: 2, sm: 2 }}
          pt={3}
          pb={2}
        >
          <Typography
            id={MODAL_TITLE_ID}
            variant="subtitle1"
            color={palette.primary.main}
            tabIndex={0}
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '150%',
              letterSpacing: '0.15px',
            }}
          >
            {STRINGS.title}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close assignments information dialog"
            sx={{
              mt: '8px',
              mr: '3px',
              color: palette.action.active,
              width: '14px',
              height: '14px',
            }}
          >
            <Icons.Close />
          </IconButton>
        </Box>

        <Box
          px={{ xs: 2, sm: 2 }}
          pb={2}
          display="flex"
          flexDirection="column"
          gap={2.5}
        >
          <LegendRow
            title={STRINGS.items[0].title}
            description={STRINGS.items[0].description}
          >
            <Box
              aria-hidden
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: palette.warning.light,
                mt: '5px',
              }}
            />
          </LegendRow>

          <LegendRow
            title={STRINGS.items[1].title}
            description={STRINGS.items[1].description}
          >
            <Box
              aria-hidden
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: palette.info.light,
                mt: '5px',
              }}
            />
          </LegendRow>

          {showHiddenLegend ? (
            <LegendRow
              title={STRINGS.items[2].title}
              description={STRINGS.items[2].description}
            >
              <Icons.VisibilityOffOutlined
                sx={{ fontSize: 20, color: palette.text.disabled }}
                aria-hidden
              />
            </LegendRow>
          ) : null}

          {showSubmittedLegend ? (
            <LegendRow
              title={STRINGS.items[3].title}
              description={STRINGS.items[3].description}
            >
              <Icons.CheckCircle
                sx={{ fontSize: 20, color: palette.success.main }}
                aria-hidden
              />
            </LegendRow>
          ) : null}
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          px={{ xs: 2, sm: 2 }}
          pb={3}
          pt={1}
        >
          <Button kind="primary" size="large" onClick={onClose}>
            {STRINGS.close}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpcomingAssignmentsInfoModal;
